import { IconButton } from '@mui/material';
import { useApi } from 'contexts/Api/Api';
import { useGame } from 'contexts/Game/Game';
import { usePage } from 'contexts/Page/Page';
import { useSettings } from 'contexts/Settings/Settings';
import NextLink from 'next/link';
import { useMemo } from 'react';
import * as setup from 'setup';
import { Container } from './Progress.styled';

export default function Progress() {
    const api = useApi();
    const game = useGame();
    const page = usePage();
    const settings = useSettings();
    // Anonymous users never reach a /lessons/* route (#171 gate), so the
    // page's own lessonParent is always the correct active item here.
    const activeLessonParent = page.lessonParent;

    const maxServerParentIndex = useMemo(() => {
        const progress = api.gameProgress['food-truck'];
        if (!progress) {
            return 0;
        }

        const checkpointId = progress.last_checkpoint_id || progress.highest_checkpoint_id;
        if (checkpointId && setup.lessonKeys.includes(checkpointId)) {
            const parent = parseInt(checkpointId, 10);
            return Math.max(setup.lessonParents.indexOf(parent), 0);
        }

        const checkpointRank = Math.max(progress.last_checkpoint_rank || 0, progress.highest_checkpoint_rank || 0);
        if (!checkpointRank) {
            return 0;
        }

        const rankedLesson = setup.lessonKeys[Math.min(checkpointRank - 1, setup.lessonKeys.length - 1)];
        const parent = parseInt(rankedLesson, 10);
        return Math.max(setup.lessonParents.indexOf(parent), 0);
    }, [api.gameProgress]);

    const items = useMemo<ProgressItem[]>(() => {
        return setup.lessonParents.map((lesson, index) => {
            const active = lesson === activeLessonParent;
            const unlockedByHistory = !!game.history?.[`${lesson}-start`];
            const unlockedByServer = index <= maxServerParentIndex;
            return {
                active,
                disabled: !!index && !unlockedByHistory && !unlockedByServer && !settings.debugProgress,
                href: `/lessons/${setup.lessons[lesson] ? lesson : setup.lessonKeys[0]}`,
                label: active ? `Current lesson ${lesson}` : `Go to lesson ${lesson}`,
            };
        });
    }, [activeLessonParent, game.history, maxServerParentIndex, settings.debugProgress]);

    return (
        <Container aria-label="Lesson progress" data-testid="progress">
            {items.map((item, index) => (
                <IconButton
                    aria-hidden={item.disabled}
                    aria-label={item.label}
                    component={NextLink}
                    data-testid={`progress-lesson-${item.href.split('/').pop()}`}
                    disabled={item.disabled}
                    href={item.href}
                    key={index}
                    size="small"
                >
                    <svg
                        aria-hidden
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="7" cy="7" r="7" fill="#fff" />
                        {(item.active || !item.disabled) && (
                            <circle
                                cx="7"
                                cy="7"
                                r={item.active ? 4 : 7}
                                fill={item.disabled ? '#fff' : setup.colors.orange}
                            />
                        )}
                    </svg>
                </IconButton>
            ))}
        </Container>
    );
}
